import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Users, CreditCard, ClipboardList, DollarSign, CalendarDays, GraduationCap, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { SubscriptionsTab } from "@/components/admin/SubscriptionsTab";
import { AgentsTab } from "@/components/admin/AgentsTab";
import { TreatmentsTab } from "@/components/admin/TreatmentsTab";
import { ImplantCatalogueTab } from "@/components/admin/ImplantSettings";

const QUICK_LINKS = [
  { to: "/forms", label: "Consultation Forms", icon: ClipboardList },
  { to: "/settings", label: "Pricing & Codes", icon: DollarSign },
  { to: "/appointments", label: "Appointments", icon: CalendarDays },
  { to: "/education", label: "Patient Education", icon: GraduationCap },
];

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [plans, setPlans] = useState([]);

  const load = () => {
    api.get("/admin/doctors").then((r) => setDoctors(r.data)).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  };
  useEffect(() => { if (user && user.is_admin) load(); }, [user]);

  if (user && !user.is_admin) { navigate("/"); return null; }

  const setActive = async (d, val) => {
    setDoctors((ds) => ds.map((x) => x.id === d.id ? { ...x, subscription_active: val } : x));
    try { await api.put(`/admin/doctors/${d.id}`, { subscription_active: val }); toast.success(`${d.name} ${val ? "activated" : "deactivated"}`); }
    catch { toast.error("Could not update"); load(); }
  };
  const setPlan = async (d, plan) => {
    try { await api.put(`/admin/doctors/${d.id}`, { plan }); setDoctors((ds) => ds.map((x) => x.id === d.id ? { ...x, plan } : x)); toast.success("Plan updated"); }
    catch { toast.error("Could not update plan"); }
  };

  return (
    <div>
      <PageHeader title="Admin Panel" subtitle="Manage doctors, subscriptions and the CONTAHA platform" breadcrumb={["Management", "Admin"]}
        actions={<StatusBadge status="info" label={<span className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" />Administrator</span>} />} />

      <Tabs defaultValue="doctors">
        <TabsList className="mb-5 h-auto flex-wrap">
          <TabsTrigger value="doctors" data-testid="admin-tab-doctors"><Users className="h-4 w-4 mr-1.5" />Doctors & Subscriptions</TabsTrigger>
          <TabsTrigger value="plans" data-testid="admin-tab-plans"><CreditCard className="h-4 w-4 mr-1.5" />Plans</TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="admin-tab-subscriptions"><CreditCard className="h-4 w-4 mr-1.5" />Subscriptions</TabsTrigger>
          <TabsTrigger value="agents" data-testid="admin-tab-agents"><Users className="h-4 w-4 mr-1.5" />Agents</TabsTrigger>
          <TabsTrigger value="treatments" data-testid="admin-tab-treatments"><DollarSign className="h-4 w-4 mr-1.5" />Treatment prices</TabsTrigger>
          <TabsTrigger value="catalogue" data-testid="admin-tab-catalogue">Implant catalogue</TabsTrigger>
          <TabsTrigger value="manage" data-testid="admin-tab-manage">Platform</TabsTrigger>
        </TabsList>

        <TabsContent value="doctors">
          <SurfaceCard className="p-2 sm:p-4">
            <div className="space-y-2" data-testid="admin-doctors-list">
              {doctors.map((d) => (
                <div key={d.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E2E8F0] dark:border-[#223139] p-3" data-testid={`admin-doctor-${d.id}`}>
                  <Avatar name={d.name} size={36} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-[#101516] dark:text-white truncate">{d.name} {d.role === "admin" && <span className="ml-1 text-[10px] font-bold text-[#00A85A]">ADMIN</span>}</div>
                    <div className="text-xs text-[#8B9698] truncate">{d.email} · {d.clinic_name || "—"}</div>
                  </div>
                  <Select value={d.plan || "none"} onValueChange={(v) => setPlan(d, v === "none" ? null : v)}>
                    <SelectTrigger className="h-9 w-36" data-testid={`admin-plan-${d.id}`}><SelectValue placeholder="No plan" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No plan</SelectItem>
                      {plans.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[#687173] dark:text-[#8B9698]">{d.subscription_active ? "Active" : "Inactive"}</span>
                    <Switch checked={!!d.subscription_active} onCheckedChange={(v) => setActive(d, v)} data-testid={`admin-active-${d.id}`} />
                  </div>
                </div>
              ))}
              {doctors.length === 0 && <p className="p-6 text-center text-sm text-[#8B9698]">No doctors yet.</p>}
            </div>
          </SurfaceCard>
        </TabsContent>

        <TabsContent value="plans">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-plans-list">
            {plans.map((p) => (
              <SurfaceCard key={p.id} className="p-5">
                <div className="text-sm font-bold text-[#101516] dark:text-white">{p.name}</div>
                <div className="mt-1 text-2xl font-extrabold text-[#00A85A]">${p.price}<span className="text-xs font-normal text-[#8B9698]">/{p.interval}</span></div>
                <ul className="mt-3 space-y-1">{(p.features || []).map((f, i) => <li key={i} className="text-xs text-[#687173] dark:text-[#8B9698]">• {f}</li>)}</ul>
              </SurfaceCard>
            ))}
          </div>
          <p className="mt-4 text-xs text-[#8B9698]">Razorpay payment integration will be added later. For now, activate doctors manually under the Doctors tab.</p>
        </TabsContent>

        <TabsContent value="subscriptions"><SubscriptionsTab /></TabsContent>
        <TabsContent value="agents"><AgentsTab /></TabsContent>
        <TabsContent value="treatments"><TreatmentsTab /></TabsContent>
        <TabsContent value="catalogue"><ImplantCatalogueTab /></TabsContent>

        <TabsContent value="manage">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {QUICK_LINKS.map((l) => (
              <SurfaceCard key={l.to} className="p-5 cursor-pointer hover:border-[#00A85A]/40" onClick={() => navigate(l.to)} data-testid={`admin-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <l.icon className="h-6 w-6 text-[#00A85A] mb-2" />
                <div className="text-sm font-bold text-[#101516] dark:text-white">{l.label}</div>
              </SurfaceCard>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
